import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  addIndexationLogosToPublicationTypes,
  addIndexationLogoToPublicationTypes,
  articlesTypesListTypes,
  dimensionsTypes,
  indexation_logoTypes,
  renameArticleTypesTypes,
} from "./dimensionTypes";
import dimensionsService from "./dimensionsService";
import { affiliationTypes } from "../affiliation/affiliationTypes";
import { ApiError, toApiError, UNKNOWN_ERROR } from "../apiError";

interface dimensionState {
  articleTypes: dimensionsTypes[] | null;
  allArticleTypes: dimensionsTypes[] | null;
  articleStatus: dimensionsTypes[] | null;
  reviewStatus: dimensionsTypes[] | null;
  authorContributions: dimensionsTypes[] | null;
  publicationReviewType: dimensionsTypes[] | null;
  contributionList: dimensionsTypes[] | null;
  affiliations: affiliationTypes[] | null;
  indexation_logo: indexation_logoTypes[] | null;
  indexationLogosByPublication: indexation_logoTypes[] | null;

  // Carga de los listados de logos (getIndexationLogo / ...ByPublication)
  loadingIndexationLogo: boolean;
  // Guardado (add/remove): separado para que los lectores del listado, como
  // IndexedIn, no vean un loading cuando un admin edita las indexaciones
  savingIndexationLogo: boolean;
  loadingArticleTypes: boolean;
  loadingAllArticleTypes: boolean;
  loadingArticleStatus: boolean;
  loadingReviewStatus: boolean;
  loadingAuthorContributions: boolean;
  loadingPublicationReviewType: boolean;
  loadingContributionList: boolean;
  loadingAffiliations: boolean;
  error: ApiError | null;
}

const initialState: dimensionState = {
  articleTypes: null,
  allArticleTypes: null,
  articleStatus: null,
  reviewStatus: null,
  authorContributions: null,
  publicationReviewType: null,
  contributionList: null,
  affiliations: null,
  indexation_logo: null,
  indexationLogosByPublication: null,
  loadingIndexationLogo: false,
  savingIndexationLogo: false,
  loadingArticleTypes: false,
  loadingAllArticleTypes: false,
  loadingArticleStatus: false,
  loadingReviewStatus: false,
  loadingAuthorContributions: false,
  loadingPublicationReviewType: false,
  loadingContributionList: false,
  loadingAffiliations: false,
  error: null,
};

export const getArticleTypes = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getArticleTypes", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getArticleTypes();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getAllArticleTypes = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getAllArticleTypes", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getAllArticleTypes();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

// El back responde con la lista completa; los activos son los que no tienen
// only_migration_flag (mismo criterio que ArticleType.get_by_publication).
export const deleteArticleTypes = createAsyncThunk<
  articlesTypesListTypes,
  string,
  { rejectValue: ApiError }
>(
  "dimensions/deleteArticleTypes",
  async (name: string, { rejectWithValue }) => {
    try {
      const all = await dimensionsService.deleteArticleTypes(name);
      return { active: all.filter((type) => !type.only_migration_flag), all };
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const renameArticleTypes = createAsyncThunk<
  articlesTypesListTypes,
  renameArticleTypesTypes,
  { rejectValue: ApiError }
>(
  "dimensions/renameArticleTypes",
  async (
    renameArticleTypesRequest: renameArticleTypesTypes,
    { rejectWithValue },
  ) => {
    try {
      const all = await dimensionsService.renameArticleTypes(
        renameArticleTypesRequest,
      );
      return { active: all.filter((type) => !type.only_migration_flag), all };
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

// El back responde solo con los activos, así que se pide la lista completa
export const addArticleTypes = createAsyncThunk<
  articlesTypesListTypes,
  string,
  { rejectValue: ApiError }
>("dimensions/addArticleTypes", async (name: string, { rejectWithValue }) => {
  try {
    const active = await dimensionsService.addArticleTypes(name);
    const all = await dimensionsService.getAllArticleTypes();
    return { active, all };
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getArticleStatus = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getArticleStatus", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getArticleStatus();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getReviewStatus = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getReviewStatus", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getReviewStatus();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getArticleAuthorContributions = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>(
  "dimensions/getArticleAuthorContributions",
  async (_, { rejectWithValue }) => {
    try {
      return await dimensionsService.getArticleAuthorContributions();
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const getPublicationReviewType = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getPublicationReviewType", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getPublicationReviewType();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getContributionList = createAsyncThunk<
  dimensionsTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getContributionList", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getContributionList();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getAffiliations = createAsyncThunk<
  affiliationTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getAffiliations", async (_, { rejectWithValue }) => {
  const attempts = sessionStorage.getItem("affiliationFetchAttempts") || "0";
  const attemptCount = parseInt(attempts, 10);
  if (attemptCount >= 1) {
    return rejectWithValue({
      message: "Límite de intentos alcanzado para obtener afiliaciones",
    });
  }
  try {
    const response = await dimensionsService.getAffiliations();
    sessionStorage.setItem("affiliationFetchAttempts", "0"); // Reiniciar intentos en caso de éxito
    return response;
  } catch (error: any) {
    sessionStorage.setItem(
      "affiliationFetchAttempts",
      (attemptCount + 1).toString(),
    );
    return rejectWithValue(toApiError(error));
  }
});

export const getIndexationLogo = createAsyncThunk<
  indexation_logoTypes[],
  void,
  { rejectValue: ApiError }
>("dimensions/getIndexationLogo", async (_, { rejectWithValue }) => {
  try {
    return await dimensionsService.getIndexationLogo();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

// Las operaciones de indexation devuelven la lista de logos de la publicación
// (o un mensaje), así que tras escribir se vuelve a pedir el listado para que
// indexationLogosByPublication siempre contenga logos.
export const addIndexationLogoToPublication = createAsyncThunk<
  indexation_logoTypes[],
  addIndexationLogoToPublicationTypes,
  { rejectValue: ApiError }
>(
  "dimensions/addIndexationLogoToPublication",
  async (request, { rejectWithValue }) => {
    try {
      await dimensionsService.addIndexationLogoToPublication(request);
      return await dimensionsService.getIndexationLogosByPublication(
        request.publication_slug,
      );
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const addIndexationLogosToPublication = createAsyncThunk<
  indexation_logoTypes[],
  addIndexationLogosToPublicationTypes,
  { rejectValue: ApiError }
>(
  "dimensions/addIndexationLogosToPublication",
  async (request, { rejectWithValue }) => {
    try {
      return await dimensionsService.addIndexationLogosToPublication(request);
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const removeIndexationLogoFromPublication = createAsyncThunk<
  indexation_logoTypes[],
  addIndexationLogoToPublicationTypes,
  { rejectValue: ApiError }
>(
  "dimensions/removeIndexationLogoFromPublication",
  async (request, { rejectWithValue }) => {
    try {
      await dimensionsService.removeIndexationLogoFromPublication(request);
      return await dimensionsService.getIndexationLogosByPublication(
        request.publication_slug,
      );
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const removeIndexationLogosFromPublication = createAsyncThunk<
  indexation_logoTypes[],
  addIndexationLogosToPublicationTypes,
  { rejectValue: ApiError }
>(
  "dimensions/removeIndexationLogosFromPublication",
  async (request, { rejectWithValue }) => {
    try {
      await dimensionsService.removeIndexationLogosFromPublication(request);
      return await dimensionsService.getIndexationLogosByPublication(
        request.publication_slug,
      );
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const getIndexationLogosByPublication = createAsyncThunk<
  indexation_logoTypes[],
  void,
  { rejectValue: ApiError }
>(
  "dimensions/getIndexationLogosByPublication",
  async (_, { rejectWithValue }) => {
    try {
      return await dimensionsService.getIndexationLogosByPublication();
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

const dimensionsSlice = createSlice({
  name: "dimensions",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getArticleTypes.fulfilled, (state, action) => {
      state.articleTypes = action.payload;
    });
    builder.addCase(getAllArticleTypes.fulfilled, (state, action) => {
      state.allArticleTypes = action.payload;
    });
    builder.addCase(getArticleStatus.fulfilled, (state, action) => {
      state.articleStatus = action.payload;
    });
    builder.addCase(getReviewStatus.fulfilled, (state, action) => {
      state.reviewStatus = action.payload;
    });
    builder.addCase(
      getArticleAuthorContributions.fulfilled,
      (state, action) => {
        state.authorContributions = action.payload;
      },
    );
    builder.addCase(getContributionList.fulfilled, (state, action) => {
      state.contributionList = action.payload;
    });
    builder.addCase(getAffiliations.fulfilled, (state, action) => {
      state.affiliations = action.payload;
    });
    builder.addCase(getPublicationReviewType.fulfilled, (state, action) => {
      state.publicationReviewType = action.payload;
    });
    builder.addCase(getIndexationLogo.fulfilled, (state, action) => {
      state.indexation_logo = action.payload;
    });

    builder.addMatcher(
      isAnyOf(
        deleteArticleTypes.fulfilled,
        renameArticleTypes.fulfilled,
        addArticleTypes.fulfilled,
      ),
      (state, action) => {
        state.articleTypes = action.payload.active;
        state.allArticleTypes = action.payload.all;
      },
    );

    builder.addMatcher(
      isAnyOf(
        getIndexationLogosByPublication.fulfilled,
        addIndexationLogoToPublication.fulfilled,
        addIndexationLogosToPublication.fulfilled,
        removeIndexationLogoFromPublication.fulfilled,
        removeIndexationLogosFromPublication.fulfilled,
      ),
      (state, action) => {
        state.indexationLogosByPublication = action.payload;
      },
    );
    const activeTypesActions = [
      getArticleTypes,
      deleteArticleTypes,
      renameArticleTypes,
      addArticleTypes,
    ];
    builder.addMatcher(
      isAnyOf(...activeTypesActions.map((a) => a.pending)),
      (state) => {
        state.loadingArticleTypes = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        ...activeTypesActions.map((a) => a.fulfilled),
        ...activeTypesActions.map((a) => a.rejected),
      ),
      (state) => {
        state.loadingArticleTypes = false;
      },
    );

    builder.addMatcher(isAnyOf(getAllArticleTypes.pending), (state) => {
      state.loadingAllArticleTypes = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getAllArticleTypes.fulfilled, getAllArticleTypes.rejected),
      (state) => {
        state.loadingAllArticleTypes = false;
      },
    );

    // Article Status
    builder.addMatcher(isAnyOf(getArticleStatus.pending), (state) => {
      state.loadingArticleStatus = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getArticleStatus.fulfilled, getArticleStatus.rejected),
      (state) => {
        state.loadingArticleStatus = false;
      },
    );

    // Review Status
    builder.addMatcher(isAnyOf(getReviewStatus.pending), (state) => {
      state.loadingReviewStatus = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getReviewStatus.fulfilled, getReviewStatus.rejected),
      (state) => {
        state.loadingReviewStatus = false;
      },
    );

    // Author Contributions
    builder.addMatcher(
      isAnyOf(getArticleAuthorContributions.pending),
      (state) => {
        state.loadingAuthorContributions = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        getArticleAuthorContributions.fulfilled,
        getArticleAuthorContributions.rejected,
      ),
      (state) => {
        state.loadingAuthorContributions = false;
      },
    );

    // Publication Review Type
    builder.addMatcher(isAnyOf(getPublicationReviewType.pending), (state) => {
      state.loadingPublicationReviewType = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(
        getPublicationReviewType.fulfilled,
        getPublicationReviewType.rejected,
      ),
      (state) => {
        state.loadingPublicationReviewType = false;
      },
    );

    // Contribution List
    builder.addMatcher(isAnyOf(getContributionList.pending), (state) => {
      state.loadingContributionList = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getContributionList.fulfilled, getContributionList.rejected),
      (state) => {
        state.loadingContributionList = false;
      },
    );

    // Affiliations
    builder.addMatcher(isAnyOf(getAffiliations.pending), (state) => {
      state.loadingAffiliations = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(getAffiliations.fulfilled, getAffiliations.rejected),
      (state) => {
        state.loadingAffiliations = false;
      },
    );

    // Indexation logos: listados (loading) y escrituras (saving) por separado
    const indexationFetchActions = [
      getIndexationLogo,
      getIndexationLogosByPublication,
    ];
    builder.addMatcher(
      isAnyOf(...indexationFetchActions.map((a) => a.pending)),
      (state) => {
        state.loadingIndexationLogo = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        ...indexationFetchActions.map((a) => a.fulfilled),
        ...indexationFetchActions.map((a) => a.rejected),
      ),
      (state) => {
        state.loadingIndexationLogo = false;
      },
    );

    const indexationSaveActions = [
      addIndexationLogoToPublication,
      addIndexationLogosToPublication,
      removeIndexationLogoFromPublication,
      removeIndexationLogosFromPublication,
    ];
    builder.addMatcher(
      isAnyOf(...indexationSaveActions.map((a) => a.pending)),
      (state) => {
        state.savingIndexationLogo = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        ...indexationSaveActions.map((a) => a.fulfilled),
        ...indexationSaveActions.map((a) => a.rejected),
      ),
      (state) => {
        state.savingIndexationLogo = false;
      },
    );

    // Solo rechazos de este slice: el prefijo evita capturar los de otros
    builder.addMatcher(
      (action) =>
        action.type.startsWith("dimensions/") &&
        action.type.endsWith("/rejected"),
      (state, action: { payload?: ApiError }) => {
        state.error = action.payload ?? UNKNOWN_ERROR;
      },
    );
  },
});

export const { resetError } = dimensionsSlice.actions;
export default dimensionsSlice.reducer;
